import { Component, input, computed, inject } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { ExternalResource, isError, isLoaded, isLoading } from '@next/arable-lib/external-resource';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ImageWithLegendAndDescriptionResponse, ImageWithLegendResponse, LegendEntry } from '../../shared/models/models';
import { LoadingCardComponent } from '../../../shared/components/loading-card.component/loading-card.component';
import { MatCard, MatCardContent } from '@angular/material/card';
import { NotificationEmptyComponent } from '@next/core-lib/components';

export interface TgaMapData {
  title: string;
  imageSrc: string;
  legendItems: TgaLegendItem[];
  legendType?: 'gradient' | 'squares';
}

export interface TgaLegendItem {
  color: string;
  label: string;
  translatedLabel?: string;
}

@Component({
    selector: 'app-tga-map-card',
    templateUrl: './tga-map-card.component.html',
    styleUrls: ['./tga-map-card.component.scss'],
    imports: [
        LoadingCardComponent,
        MatCard,
        MatCardContent,
        NotificationEmptyComponent,
        TranslatePipe,
    ],
})
export class TgaMapCardComponent {
  mapData$ = input<Observable<ExternalResource<ImageWithLegendResponse | ImageWithLegendAndDescriptionResponse>>>();
  mapTitle = input.required<string>();
  legendType = input<'gradient' | 'squares'>('squares');

  private readonly localeService = inject(LocaleService);

  // Convert locale change observable to signal for reactivity
  private readonly localeChanged = toSignal(this.localeService.localeDataChanged$);

  // Convert the input signal (which contains an Observable) to a flattened observable
  // toObservable converts Signal<Observable<T>> to Observable<Observable<T>>
  // switchMap flattens it to Observable<T>
  // toSignal converts Observable<T> to Signal<T>
  // This avoids calling toSignal inside a reactive context
  mapDataResource = toSignal(
    toObservable(this.mapData$).pipe(
      switchMap((obs$) => obs$ || of(undefined)),
    ),
    { initialValue: undefined },
  );

  // Computed signal for legend entries with translated labels (gradient legend)
  translatedDisplayLabels = computed(() => {
    // React to locale changes
    this.localeChanged();

    const resource = this.mapDataResource();
    if (!resource || !isLoaded(resource)) return [];

    const displayLabels = this.getDisplayLabels(resource.data.legendData.entries);
    return displayLabels.map((entry) => ({
      ...entry,
      translatedLabel: this.translateLabel(entry.label),
    }));
  });

  // Computed signal for all legend entries with translated labels (squares legend)
  translatedAllLabels = computed(() => {
    // React to locale changes
    this.localeChanged();

    const resource = this.mapDataResource();
    if (!resource || !isLoaded(resource)) return [];

    return resource.data.legendData.entries.map((entry) => ({
      ...entry,
      translatedLabel: this.translateLabel(entry.label),
    }));
  });

  readonly isLoading = isLoading;
  readonly isLoaded = isLoaded;
  readonly isError = isError;

  getGradientBackground(mapData: TgaMapData): string {
    const colors = mapData.legendItems.map((item) => item.color);
    return `linear-gradient(to right, ${colors.join(', ')})`;
  }

  mapDataFromResponse(elevationData: ImageWithLegendResponse): TgaMapData {
    return {
      title: this.mapTitle(),
      imageSrc: `data:${elevationData.contentType};base64,${elevationData.imageData}`,
      legendType: this.legendType(),
      legendItems: elevationData.legendData.entries.map((entry) => ({
        color: entry.color,
        label: entry.label,
      })),
    }
  }

  /**
   * Calculate the position percentage for a label in the gradient legend
   * Uses edge positioning to align with gradient bar while keeping text within card bounds
   * Positions first label at 1% and last label at 99% to align with gradient edges
   * CSS adjustments ensure text stays within card bounds even at edges
   * Only considers entries that have labels
   */
  getLabelPosition(entry: LegendEntry, allEntries: LegendEntry[]): number {
    const displayLabels = this.getDisplayLabels(allEntries);
    const index = displayLabels.findIndex((e) => e.label === entry.label);
    const totalDisplayEntries = displayLabels.length;

    if (totalDisplayEntries === 1) return 50; // Single entry, center it

    // Position slightly inward from edges: 1% and 99% for better text containment
    if (index === 0) return 1; // Slightly inward from left edge
    if (index === totalDisplayEntries - 1) return 99; // Slightly inward from right edge

    // For middle labels, distribute evenly between 0% and 100%
    const middleRange = 100; // 100% - 0% = 100%
    const middleSteps = totalDisplayEntries - 1;
    const stepSize = middleRange / middleSteps;

    return 0 + index * stepSize;
  }

  private translateLabel(label: string): string {
    const translationKey = `TGA.MapLegend.${label}`;
    const translated = this.localeService.translate(translationKey);

    if (translated === translationKey) {
      return label;
    }
    return translated;
  }

  /**
   * Select optimal labels for display to prevent overcrowding
   * Always shows: start, end, middle, and up to 2 intermediate labels
   * Maximum of 5 labels total for clean display
   */
  getDisplayLabels(allEntries: LegendEntry[]): LegendEntry[] {
    const filteredEntries = allEntries.filter((e) => !!e.label.trim());
    const totalEntries = filteredEntries.length;

    if (totalEntries <= 5) {
      return filteredEntries;
    }

    const selectedIndices = new Set<number>();

    // Always include first and last
    selectedIndices.add(0);
    selectedIndices.add(totalEntries - 1);

    // Always include middle
    const middleIndex = Math.floor(totalEntries / 2);
    selectedIndices.add(middleIndex);

    // Add two intermediate labels
    const quarterIndex = Math.floor(totalEntries / 4);
    const threeQuarterIndex = Math.floor((3 * totalEntries) / 4);

    selectedIndices.add(quarterIndex);
    selectedIndices.add(threeQuarterIndex);

    return Array.from(selectedIndices)
      .sort((a, b) => a - b)
      .map((index) => filteredEntries[index]);
  }

  isImageWithDescription(
    data: ImageWithLegendResponse | ImageWithLegendAndDescriptionResponse,
  ): data is ImageWithLegendAndDescriptionResponse {
    return !!(data as ImageWithLegendAndDescriptionResponse).description;
  }

}
